import { useState, useEffect } from 'react'
import Header from '@components/Header/Header'
import PhoneInput from '@components/PhoneInput/PhoneInput'
import NumericKeyboard from '@components/NumericKeyboard/NumericKeyboard'
import PrimaryButton from '@components/PrimaryButton/PrimaryButton'
import LoadingModal from '@components/LoadingModal/LoadingModal'
import Snackbar from '@components/Snackbar/Snackbar'
import { useNavigate, useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'
import { usePhoneValidation } from '@hooks/usePhoneValidation'
import { usePostpaidInquiry } from '@hooks/usePostpaidInquiry'
import { usePpobProducts } from '@hooks/usePpobProducts'
import { PpobProductCategory } from '@hooks/usePpobServices'
import { setHeaderColor } from '@lib/telegram'
import { WHITE } from '@styles/colors'
import {
  Container,
  ContentContainer,
  PhoneInputWrapper,
  KeyboardSection
} from './PhoneBill.styles'
import AlertBox from '@components/AlertBox/AlertBox'

const PhoneBill = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const { validatePhoneNumber, isValidating, normalizePhoneNumber } = usePhoneValidation()
  const { inquirePostpaid, isLoading: isInquiring, } = usePostpaidInquiry()
  const { fetchProducts } = usePpobProducts()

  const state = location.state as { category?: PpobProductCategory } | undefined
  const category = state?.category

  const [phoneNumber, setPhoneNumber] = useState('')
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [showSnackbar, setShowSnackbar] = useState(false)

  useEffect(() => {
    setHeaderColor(WHITE)
  }, [])

  const handleKeyboardChange = (value: string | 'delete') => {
    if (value === 'delete') {
      setPhoneNumber(prev => prev.slice(0, -1))
    } else {
      setPhoneNumber(prev => prev + value)
    }
  }

  const handleContinue = async () => {
    // Validate phone number first
    const validation = await validatePhoneNumber(phoneNumber, category)

    if (validation.success) {
      // Save normalized phone number for payment
      const normalized = normalizePhoneNumber(phoneNumber)

      // Fetch postpaid products for this provider to get the buyer SKU code
      const productsResponse = await fetchProducts({
        brand: validation.providerName,
        category: category,
        size: 1
      })

      if (!productsResponse || productsResponse.result.length === 0) {
        setSnackbarMessage(t('phoneBill.errors.noProductsAvailable'))
        setShowSnackbar(true)
        return
      }

      const buyerSkuCode = productsResponse.result[0].buyerSkuCode

      // Inquire postpaid bill to get the amount
      const inquiryResult = await inquirePostpaid(buyerSkuCode, normalized)

      if (inquiryResult) {
        // Navigate to payment confirm page with inquiry result including ref_id, customer_no, and buyer_sku_code
        navigate('/payment-confirm', {
          state: {
            phoneNumber: normalized,
            providerName: validation.providerName,
            providerImageUrl: validation.providerImageUrl,
            amount: inquiryResult.selling_price,
            amountNet: inquiryResult.selling_price_net,
            paymentType: 'postpaid',
            refId: inquiryResult.ref_id,
            customerNo: inquiryResult.customer_no,
            buyerSkuCode: buyerSkuCode,
            inquiryQuote: inquiryResult.ppob_inquiry_quote_uuid,
            adminFee: inquiryResult.admin_fee,
            platformFee: inquiryResult.platform_fee
          }
        })
      } else {
        setSnackbarMessage(t('phoneBill.errors.failedToGetBill'))
        setShowSnackbar(true)
      }
    } else {
      setSnackbarMessage(t('phoneBill.errors.invalidPhoneNumber'))
      setShowSnackbar(true)
    }
  }

  const handleSnackbarClose = () => {
    setShowSnackbar(false)
  }

  const handleBack = () => {
    navigate(-1)
  }

  return (
    <Container>
      <LoadingModal visible={isValidating || isInquiring} />
      <Snackbar
        message={snackbarMessage}
        isVisible={showSnackbar}
        onClose={handleSnackbarClose}
        textAlign="center"
        width="100%"
      />
      <Header
        title={t('phoneBill.header')}
        showBackButton={true}
        onBackClick={handleBack}
      />
      <ContentContainer>
        <PhoneInputWrapper>
          <PhoneInput
            value={phoneNumber}
            onChange={setPhoneNumber}
            readOnly={false}
            iconType='credit'
          />
        </PhoneInputWrapper>
        <AlertBox
          alertText={t('phoneBill.alertText')}
        />

        <KeyboardSection>
          <NumericKeyboard onChange={handleKeyboardChange} />
          <PrimaryButton
            onClick={handleContinue}
            disabled={!phoneNumber}
          >
            {t('common.continue')}
          </PrimaryButton>
        </KeyboardSection>
      </ContentContainer>
    </Container>
  )
}

export default PhoneBill
